import { useMemo } from 'react';
import { DataTable, type Column, type FilterConfig, type RowAction } from './DataTable';
import { RefreshCw } from 'lucide-react';
import type { PullRequest } from '../types';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { PageHeader } from './ui/page';

interface DashboardProps {
  prs: PullRequest[];
  onSelectPr: (prId: string) => void;
  onResetDb: () => void;
  loading: boolean;
}

/**
 * PR list dashboard — the historical home view (separate from
 * DashboardOverview, which is the operational KPI page). Same router-style
 * primitives: PageHeader + Card metric tiles + Badge for status.
 */

const STATUS_META: Record<PullRequest['status'], { dot: string; label: string }> = {
  open: { dot: '#2383E2', label: 'Queue Pending' },
  reviewed: { dot: '#2E7D32', label: 'Reviewed' },
  merged: { dot: '#9065B0', label: 'Merged' },
  closed: { dot: '#91918E', label: 'Closed' },
};

export function Dashboard({ prs, onSelectPr, onResetDb, loading }: DashboardProps) {
  const showSkeletons = loading && prs.length === 0;

  // Compute high level analytics
  const totalPrs = prs.length;
  const reviewedPrs = prs.filter(p => p.status === 'reviewed');
  const openPrs = prs.filter(p => p.status === 'open');

  const reviewedCount = reviewedPrs.length;
  const avgScore = reviewedCount > 0
    ? Math.round(reviewedPrs.reduce((acc, p) => acc + (p.reviewStatus?.score || 0), 0) / reviewedCount)
    : 0;

  const totalLoopsAutomated = reviewedPrs.reduce((acc, p) => acc + (p.reviewStatus?.loopCount || 0), 0);
  const totalTestsRun = reviewedPrs.reduce((acc, p) => acc + (p.reviewStatus?.testsRun || 0), 0);
  const totalTestsPassed = reviewedPrs.reduce((acc, p) => acc + (p.reviewStatus?.testsPassed || 0), 0);
  const testPassRate = totalTestsRun > 0 ? Math.round((totalTestsPassed / totalTestsRun) * 100) : 0;

  const repoOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of prs) {
      const name = p.repoName;
      if (name) seen.set(name, name);
    }
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [prs]);

  const prFilters: FilterConfig<PullRequest>[] = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      options: (Object.keys(STATUS_META) as PullRequest['status'][]).map((s) => ({ value: s, label: STATUS_META[s].label })),
      predicate: (p, v) => p.status === v,
    },
    ...(repoOptions.length > 1
      ? [{ key: 'repo', label: 'Repository', options: repoOptions, predicate: (p: PullRequest, v: string) => p.repoName === v }]
      : []),
  ], [repoOptions]);

  const prColumns: Column<PullRequest>[] = [
    {
      key: 'pr',
      header: 'Pull Request',
      render: (pr) => (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              onClick={(e) => { e.stopPropagation(); onSelectPr(pr.id); }}
              className="font-semibold hover:underline cursor-pointer transition text-foreground"
            >
              {pr.title}
            </span>
            <Badge className="font-mono bg-primary/10 text-primary border-primary/20">
              {pr.branch}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <img src={pr.authorAvatar} alt={pr.author} className="w-4 h-4 rounded-full object-cover" referrerPolicy="no-referrer" />
            <span>{pr.author}</span>
            <span>•</span>
            <span>{pr.targetBranch}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (pr) => {
        const meta = STATUS_META[pr.status] ?? STATUS_META.open;
        return (
          <span className="flex items-center gap-2 text-xs text-foreground">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: meta.dot }} />
            {meta.label}
          </span>
        );
      },
    },
    {
      key: 'steps',
      header: 'Steps',
      render: (pr) => (
        <span className="font-mono text-xs text-muted-foreground">
          {pr.status === 'reviewed' ? `${pr.reviewStatus?.loopCount ?? 0} loops` : '—'}
        </span>
      ),
    },
    {
      key: 'score',
      header: 'Score',
      render: (pr) => pr.status === 'reviewed' && pr.reviewStatus ? (
        <Badge className="font-semibold bg-information/10 text-information border-information/20">
          {pr.reviewStatus.score}/100
        </Badge>
      ) : <span className="text-muted-foreground">Not reviewed</span>,
    },
  ];

  const prActions: RowAction<PullRequest>[] = [
    {
      key: 'open',
      label: (pr) => pr.status === 'open' ? 'Run CLI' : 'View Report',
      onClick: (pr) => onSelectPr(pr.id),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Pull Requests"
        title="Review queue & sandboxes"
        description="All open and reviewed pull requests tracked by the autonomous review pipeline."
        action={
          <Button
            variant="secondary"
            onClick={onResetDb}
            disabled={loading}
            id="btn-reset-queue"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            Reset Demos
          </Button>
        }
      />

      {/* Metrics Grid — uses router's metric-tile shape (Card + CardContent). */}
      {showSkeletons ? (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" aria-busy="true">
          <MetricSkeleton /><MetricSkeleton /><MetricSkeleton /><MetricSkeleton />
        </section>
      ) : (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Automated Reviews" value={`${reviewedCount}`} suffix={`/ ${totalPrs} PRs`} sub={`${openPrs.length} pending review queue actions`} />
          <MetricCard label="Feedback Loops" value={`${totalLoopsAutomated}`} suffix="runs" sub="Self-correction loops active" />
          <MetricCard label="Average PR Score" value={avgScore > 0 ? `${avgScore}%` : '—'} sub="Quality standard set" />
          <MetricCard label="Verify Success" value={`${testPassRate}%`} sub={`${totalTestsPassed} / ${totalTestsRun} assertions verified`} />
        </section>
      )}

      {/* Queue & Sandbox Table */}
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-border text-sm font-bold tracking-tight uppercase text-foreground">
            Review Queue & Sandboxes
          </div>
          <DataTable
            rows={prs}
            columns={prColumns}
            rowKey={(pr) => pr.id}
            actions={prActions}
            loading={loading && prs.length === 0}
            searchKeys={[(pr) => pr.title, (pr) => pr.branch, (pr) => pr.author]}
            searchPlaceholder="Search pull requests…"
            filters={prFilters}
            pageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ label, value, suffix, sub }: {
  label: string; value: string; suffix?: string; sub?: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="text-[26px] font-extrabold text-foreground mt-2 leading-none">
          {value}
          {suffix && <span className="text-sm font-normal text-muted-foreground ml-1">{suffix}</span>}
        </div>
        {sub && <div className="text-[11px] text-muted-foreground mt-2">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function MetricSkeleton() {
  return (
    <Card>
      <CardContent className="p-5 space-y-3" aria-hidden="true">
        <div className="h-3 w-24 rounded bg-muted animate-pulse" />
        <div className="h-7 w-16 rounded bg-muted animate-pulse" />
        <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
      </CardContent>
    </Card>
  );
}
